// src/features/mocktests/components/SpeakingSection.js

import React, { useState, useRef } from "react";
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTestNavigation } from "../../../utils/testNavigation";
import {
  Box,
  Button,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Popover,
  CircularProgress,
  Badge,
  Fade,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  ExpandLess,
  ExpandMore,
  CheckRounded as CheckRoundedIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";

import {
  useUploadAudioMutation,
  useCreateSpeakingAnswerMutation,
} from "../mockTestSlice";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import HtmlRenderer from "./HtmlRenderer";

/* ---------------------------------------------------------------
   Enhanced Gradient Countdown (Ring + Pie + Center Icon)
--------------------------------------------------------------- */
const EnhancedCountdown = ({
  size = 56,
  stroke = 5,
  progress = 0, // 0..100
  showPie = true, // show inside pie fill
  showRing = true, // show outer progress ring
  idSuffix = "speaking",
}) => {
  const p = Math.min(100, Math.max(0, progress));
  const finished = p >= 100;

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c;
  const offset = c * (1 - p / 100);

  // ------- PIE PATH (sector fill) -------
  const angle = (p / 100) * Math.PI * 2;
  const cx = size / 2;
  const cy = size / 2;
  const x = cx + r * Math.cos(-Math.PI / 2 + angle);
  const y = cy + r * Math.sin(-Math.PI / 2 + angle);
  const largeArc = angle > Math.PI ? 1 : 0;
  const piePath =
    p <= 0
      ? ""
      : `
    M ${cx} ${cy}
    L ${cx} ${cy - r}
    A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
    Z
  `;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`grad-${idSuffix}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9D2235" />
            <stop offset="100%" stopColor="#5d1220" />
          </linearGradient>
          <radialGradient id={`soft-${idSuffix}`} cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="rgba(157,34,53,0.10)" />
            <stop offset="100%" stopColor="rgba(157,34,53,0.0)" />
          </radialGradient>
        </defs>

        {/* Soft background plate for a card-ish look */}
        <circle cx={cx} cy={cy} r={r} fill={`url(#soft-${idSuffix})`} />

        {/* Track */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(157,34,53,0.18)"
            strokeWidth={stroke}
          />
        )}

        {/* Pie fill from center (sector) */}
        {showPie && p > 0 && (
          <path d={piePath} fill={`url(#grad-${idSuffix})`} />
        )}

        {/* Progress Ring on top */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={`url(#grad-${idSuffix})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}

        {/* Subtle glow when finished */}
        {finished && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#9D2235"
            strokeOpacity="0.25"
            strokeWidth={2}
          />
        )}
      </svg>

      {/* Center icon */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 180ms ease",
          transform: finished ? "scale(1.05)" : "scale(1)",
        }}
      >
        {finished ? (
          <CheckRoundedIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        ) : (
          <AccessTimeIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        )}
      </Box>
    </Box>
  );
};

/* ---------------------------------------------------------------
   SpeakingSection Component
--------------------------------------------------------------- */

export default function SpeakingSection({
  speakingData,
  currentPart = 1,
  studentAttemptId,
  speakingTestId,
  setLocalSpeakingData,
  setCurrentPart,
  setCurrentOffset,
  navigate,
  speakingCardIds = [],
  currentCardIndex = 0,
  setCurrentCardIndex,
  allSpeakingAudios,
  setAllSpeakingAudios,
  isAiBased = false,
  isSubmitted = false,
  updateTestAttempt = null,
  speakingAudioMethod = null, // "record" or "upload"
  setSpeakingTestCompleted = null, // Function to set completion flag
  speakingTestCompleted = false, // Current completion status
  onSpeakingCompletion = null, // Callback for when speaking test is completed
  instrOpen = false, // Whether instructions popup is currently showing
}) {
  // Notes functionality
  const [notes, setNotes] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  const handleAddNote = (noteData) => {
    setNotes((prevNotes) => [
      ...prevNotes,
      {
        ...noteData,
        timestamp: new Date().toISOString(),
        part: `Speaking Part ${currentPart}`,
        cardIndex: currentCardIndex,
      },
    ]);
  };
  const { testId } = useParams();
  const navigationRef = useRef(navigate);
  const instrShownRef = useRef(false);
  const { baseRoute, testType } = useTestNavigation();

  // Update navigation ref when navigate changes
  useEffect(() => {
    navigationRef.current = navigate;
  }, [navigate]);

  // Handle browser back button - navigate to main screen
  useEffect(() => {
    const handlePopState = (event) => {
      // Prevent default back navigation
      event.preventDefault();
      // Navigate to main mock test screen
      navigationRef.current(`${baseRoute}/${testId}`);
    };

    // Add popstate event listener
    window.addEventListener("popstate", handlePopState);

    // Push current state to allow back button to trigger our handler
    window.history.pushState(null, "", window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [testId, baseRoute]);

  // Timer states (must be inside function body)
  const [prepTimeLeft, setPrepTimeLeft] = useState(180); // 3 minutes in seconds
  const [prepActive, setPrepActive] = useState(false); // starts only after instructions close

  // Recording timer states
  const [recordTimeLeft, setRecordTimeLeft] = useState(360); // 6 minutes in seconds
  const [recordTimerActive, setRecordTimerActive] = useState(false);

  // Timer progress calculation for enhanced countdown
  const getSpeakingTimerProgress = () => {
    if (prepActive) {
      // During preparation time: show progress of 3-minute prep timer
      const totalPrepTime = 180; // 3 minutes
      return ((totalPrepTime - prepTimeLeft) / totalPrepTime) * 100;
    } else {
      // During recording time: show progress of 6-minute record timer
      const totalRecordTime = 360; // 6 minutes
      return ((totalRecordTime - recordTimeLeft) / totalRecordTime) * 100;
    }
  };

  const speakingTimerProgress = getSpeakingTimerProgress();

  // Format timer display for header
  const formatSpeakingTimerDisplay = () => {
    const timeLeft = prepActive ? prepTimeLeft : recordTimeLeft;
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  // Start prep timer when instructions popup closes
  useEffect(() => {
    if (instrOpen) {
      instrShownRef.current = true;
    } else if (instrShownRef.current) {
      instrShownRef.current = false;
      setPrepActive(true);
    }
  }, [instrOpen]);

  // Fallback: start prep timer if instructions were never shown within 5 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!instrShownRef.current) {
        setPrepActive(true);
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Preparation timer effect
  useEffect(() => {
    if (!prepActive || isSubmitted) return; // Stop timer if already submitted
    if (prepTimeLeft <= 0) {
      setPrepActive(false);
      setPrepEndedPopupOpen(true); // Show popup when prep time naturally ends
      return;
    }
    const timer = setInterval(() => {
      setPrepTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [prepActive, prepTimeLeft, isSubmitted]);

  // Recording / Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Recording timer effect (must be after isRecording is defined)
  useEffect(() => {
    // Only run timer if actively recording and not paused
    if (!recordTimerActive || !isRecording || isPaused) return;
    if (recordTimeLeft <= 0) {
      setRecordTimerActive(false);
      setRecordTimeLeft(360);
      setRecordEndedPopupOpen(true); // Show popup when 6 minutes of recording is done
      if (isRecording) {
        handleStop();
      }
      return;
    }
    const timer = setInterval(() => {
      setRecordTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [recordTimerActive, recordTimeLeft, isRecording, isPaused]);

  // Audio upload + speaking submission
  const [audioMeta, setAudioMeta] = useState(null);
  const [uploadAudio, { isLoading: isUploading }] = useUploadAudioMutation();
  const [createSpeakingAnswer, { isLoading: isSubmitting }] =
    useCreateSpeakingAnswerMutation();

  // This ref holds ALL audios for submit

  // UI feedback
  const [successOpen, setSuccessOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // New popup states for API response
  const [submissionPopupOpen, setSubmissionPopupOpen] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState(null);

  // Timer notification popups
  const [prepEndedPopupOpen, setPrepEndedPopupOpen] = useState(false);
  const [recordEndedPopupOpen, setRecordEndedPopupOpen] = useState(false);

  // Highlighting and notes functionality now handled by TextHighlighterWrapper

  // Helpers
  function resetCurrentPartState() {
    setUploadedFile(null);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setIsRecording(false);
    setIsPaused(false);
    setAudioMeta(null);
    setUploadProgress(0);
    setIsPlaying(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }

  // Skip preparation time
  const handleSkipPreparation = () => {
    setPrepActive(false);
    setPrepTimeLeft(0);
  };

  // Audio playback controls
  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  // Recording handlers
  const handleStart = () => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      setRecordTimerActive(true);
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const recorder = new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];
        recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          setRecordedBlob(blob);
          setRecordedUrl(URL.createObjectURL(blob));
        };
        recorder.start();
        setIsRecording(true);
        setRecordTimerActive(true);
        setRecordTimeLeft(360); // Reset timer on new recording
      })
      .catch(() => {
        const errorResponse = {
          status: "error",
          success: false,
          message: "Error accessing microphone. Please check your permissions.",
          errors: ["Microphone access denied"],
        };
        setSubmissionResponse(errorResponse);
        setSubmissionPopupOpen(true);
      });
  };

  const handlePause = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
    }
  };

  const handleStop = () => {
    if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
    }
  };
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check duration before accepting
      const audio = document.createElement("audio");
      audio.src = URL.createObjectURL(file);
      audio.onloadedmetadata = () => {
        if (audio.duration > 360) {
          const errorResponse = {
            status: "error",
            success: false,
            message: "Audio file must be 6 minutes or less.",
            errors: ["File duration exceeds maximum limit"],
          };
          setSubmissionResponse(errorResponse);
          setSubmissionPopupOpen(true);
          if (fileInputRef.current) fileInputRef.current.value = "";
        } else {
          setUploadedFile(file);
          setRecordedBlob(null);
          setRecordedUrl(audio.src);
        }
      };
    }
  };

  const handleDeleteRecording = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setUploadedFile(null);
    setAudioMeta(null);
    setUploadProgress(0);
    setRecordTimeLeft(360); // Reset timer to 6:00
    setRecordTimerActive(false); // Stop timer
    setIsPlaying(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // AUDIO UPLOAD FUNCTION
  const handleUploadAudio = async () => {
    try {
      setUploadProgress(0);
      let formData = new FormData();
      if (uploadedFile) {
        formData.append("audio", uploadedFile, uploadedFile.name);
      } else if (recordedBlob) {
        formData.append("audio", recordedBlob, `recorded-${Date.now()}.webm`);
      } else {
        // Create error response for missing audio
        const errorResponse = {
          status: 400,
          data: {
            success: 0,
            message: "Please record or upload audio first.",
            error: "No audio file provided",
            data: {},
          },
        };
        setSubmissionResponse(errorResponse);
        setSubmissionPopupOpen(true);
        return null;
      }
      // Upload to server
      const res = await uploadAudio(formData).unwrap();
      setUploadProgress(100);

      // Flatten audio object for uniformity
      let flatAudioObj =
        res?.uploadToBucket?.[0] || res?.data?.uploadToBucket?.[0] || res;

      setAudioMeta(flatAudioObj);
      // Audio upload success - no popup needed here as this is intermediate step
      return flatAudioObj;
    } catch (err) {
      // Create error response for upload failure
      const errorResponse = {
        status: err.status || 500,
        data: {
          success: 0,
          message: "Audio upload failed. Please try again.",
          error: err.message || "Upload error occurred",
          data: err.data || {},
        },
      };
      setSubmissionResponse(errorResponse);
      setSubmissionPopupOpen(true);
      return null;
    }
  };

  // SUBMIT/NEXT FUNCTION
  const handleNextOrSubmit = async () => {
    let audioRes = audioMeta;
    if (!audioRes) {
      audioRes = await handleUploadAudio();
      if (!audioRes) return;
    }

    // Always flatten
    let flatAudioObj = audioRes?.uploadToBucket?.[0] || audioRes;

    // Update the parent's audio array for every part
    setAllSpeakingAudios((prev) => [...prev, flatAudioObj]);

    resetCurrentPartState();

    // If NOT the last card, move to next
    if (currentCardIndex < speakingCardIds.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setCurrentPart && setCurrentPart((prev) => prev + 1);
      setCurrentOffset && setCurrentOffset((prev) => prev + 1);
      setAudioMeta(null);
    } else if (currentCardIndex === speakingCardIds.length - 1) {
      try {
        // submit ALL audios accumulated in parent
        const submitPayload = {
          studentAttemptId,
          speakingTestId,
          isAiBased,
          speakingAudios: [...allSpeakingAudios, flatAudioObj], // include last one
        };
        const response = await createSpeakingAnswer(submitPayload).unwrap();

        // Check if the response contains an error message (even with status 200)
        if (
          response?.data?.message &&
          response.data.message.includes("already completed")
        ) {
          console.log(
            "Speaking test already completed, showing popup and setting completion flag"
          );
          // Show popup with the error message instead of success
          setSubmissionResponse(response);
          setSubmissionPopupOpen(true);
          // Set completion flag to prevent further API calls
          setSpeakingTestCompleted && setSpeakingTestCompleted(true);
          return; // Exit early, don't proceed with success flow
        }

        console.log(
          "Speaking submission successful, setting completion flag"
        );
        
        // Set completion flag to prevent further API calls
        setSpeakingTestCompleted && setSpeakingTestCompleted(true);

        // Update test attempt completion status for simulation mode
        if (updateTestAttempt && studentAttemptId) {
          try {
            console.log(
              "Updating test attempt completion for simulation mode:",
              studentAttemptId
            );
            await updateTestAttempt({
              studentAttemptId,
              isCompleted: true,
            });
            console.log("Test attempt updated successfully");
          } catch (error) {
            console.error("Error updating test attempt:", error);
            // Don't fail the submission if this update fails
          }
        }

        // Reset states but don't change currentCardIndex to avoid triggering useEffect
        setAllSpeakingAudios([]); // reset after submit
        resetCurrentPartState();
        setAudioMeta(null);
        
        // Call completion handler directly (for simulation mode to show results)
        if (onSpeakingCompletion) {
          await onSpeakingCompletion();
        } else {
          // Default behavior - navigate back
          navigate(`${baseRoute}/${testId}`);
        }
        // setCurrentOffset && setCurrentOffset(0);
        // setCurrentCardIndex && setCurrentCardIndex(0);
      } catch (err) {
        console.error("Speaking submission error:", err);

        // Create an error response object to show in popup
        const errorResponse = {
          status: err.status || 500,
          data: {
            success: 0,
            message:
              err.data?.message ||
              "Failed to submit speaking answer. Please try again.",
            error: err.message || "Network or server error occurred",
            data: err.data || {},
          },
        };

        // Show error in popup instead of snackbar
        setSubmissionResponse(errorResponse);
        setSubmissionPopupOpen(true);

        // Still set completion flag to prevent further API calls
        setSpeakingTestCompleted && setSpeakingTestCompleted(true);
      }
    }
  };

  // Handle closing the submission popup and navigating
  const handleCloseSubmissionPopup = async () => {
    setSubmissionPopupOpen(false);
    setSubmissionResponse(null);
    
    // Call completion handler if provided (for simulation mode to show results)
    if (onSpeakingCompletion) {
      await onSpeakingCompletion();
    } else {
      // Default behavior - navigate back
      setTimeout(() => {
        navigate(`${baseRoute}/${testId}`);
      }, 500);
    }
  };

  // --- UI and Progress ---
  const speakingTest = speakingData?.speakingTest;
  const speakingMainCards = speakingTest?.speakingMainCards || [];
  const currentMainCard =
    speakingMainCards.find((card) => card.mainCardNumber === currentPart) ||
    speakingMainCards[currentPart - 1] ||
    speakingMainCards[0] ||
    {};

  const speakingCards = currentMainCard?.speakingCard || [];
  const totalParts = speakingCardIds.length || 2;
  const isLastCard = currentCardIndex === totalParts - 1;

  // If test is completed and no speaking data, show a minimal interface
  if (speakingTestCompleted && !speakingData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5" sx={{ color: "#9D2235", mb: 2 }}>
          Speaking Test Completed
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Your speaking responses have been submitted.
        </Typography>

        {/* Show the popup dialogs even with minimal interface */}
        {/* Submission Response Popup */}
        <Dialog
          open={submissionPopupOpen}
          onClose={handleCloseSubmissionPopup}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: "80vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              bgcolor: submissionResponse?.data?.message?.includes(
                "already completed"
              )
                ? "#f44336"
                : "#9D2235",
              color: "white",
              py: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                {submissionResponse?.data?.message?.includes(
                  "already completed"
                )
                  ? "Submission Notice"
                  : "Submission Successful!"}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {submissionResponse?.data?.message?.includes(
              "already completed"
            ) ? (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold">
                  ⚠️ Test Already Completed
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {submissionResponse.data.message}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold">
                  🎉 All speaking parts have been recorded and submitted
                  successfully!
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Your audio responses are now being processed for evaluation.
                </Typography>
              </Alert>
            )}

            {submissionResponse && (
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "#9D2235", fontWeight: "bold" }}
                >
                  📋 Speaking Answer API Response:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, fontStyle: "italic", color: "text.secondary" }}
                >
                  Response from:{" "}
                  <code>/api/v1/en/student/speaking-answer/create</code>
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#f8f9fa",
                    p: 2,
                    borderRadius: 2,
                    maxHeight: "300px",
                    overflow: "auto",
                    border: "2px solid #e9ecef",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontFamily: '"Courier New", Consolas, monospace',
                      fontSize: "0.85rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                      color: "#2d3748",
                    }}
                  >
                    {JSON.stringify(submissionResponse, null, 2)}
                  </pre>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1,
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  💡 This shows the server response from the speaking answer
                  submission API.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: "center" }}>
            <Button
              onClick={handleCloseSubmissionPopup}
              variant="contained"
              size="large"
              startIcon={<CheckCircleIcon />}
              sx={{
                backgroundColor: "#9D2235",
                "&:hover": {
                  backgroundColor: "#7a182a",
                },
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              🏠 Return to Test Menu
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <>
      {/* ===== Header ===== */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          bgcolor: "#F7F7F7",
          px: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5 },
          py: { xs: 0.1 },
          position: "sticky",
          top: 0,
          zIndex: 1000,
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Card
          elevation={1}
          sx={{
            width: "100%",
            mb: 1,
            p: { xs: 0.2, sm: 0.8, md: 1, lg: 1.2 },
            bgcolor: "#fff",
            borderRadius: { xs: 0.8, sm: 1.5, md: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              gridTemplateAreas: {
                xs: `"left" "center" "right"`,
                sm: `"left center right"`,
                md: `"left center right"`,
                lg: `"left center right"`,
              },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 2fr 1fr",
                md: "1fr 2fr 1fr",
                lg: "1fr 2fr 1fr",
              },
              gridTemplateRows: { xs: "auto auto auto", sm: "auto" },
              columnGap: { xs: 0, sm: 0.8, md: 1.2, lg: 1.5 },
              rowGap: { xs: 0.3, sm: 0 },
              minWidth: 0,
              minHeight: { xs: 20, sm: 45, md: 50 },
            }}
          >
            {/* LEFT - Speaking Info */}
            <Box
              sx={{
                gridArea: "left",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                gap: { xs: 0.3, sm: 1, md: 1.2, lg: 1.5 },
                py: { xs: 0.1, sm: 0.2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 0,
                  flexShrink: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#9D2235",
                    fontSize: { xs: "0.6rem", sm: "0.85rem", md: "0.95rem" },
                    lineHeight: 1.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Speaking Module
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#7a7a7a",
                    fontSize: { xs: "0.55rem", sm: "0.75rem", md: "0.8rem" },
                    lineHeight: 1.1,
                    letterSpacing: 0.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <b>Part {currentCardIndex + 1}</b>
                </Typography>
              </Box>
            </Box>

            {/* CENTER - Speaking Navigation Chips */}
            <Box
              sx={{
                gridArea: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: { xs: 0.2, sm: 0.8, md: 1.2, lg: 1.5 },
                minWidth: 0,
                justifyContent: "center",
                py: { xs: 0.1, sm: 0.3, md: 0.4 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  px: 0,
                  py: 0,
                  minWidth: { xs: "100%", sm: "auto" },
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    gap: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5 },
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {speakingCardIds.map((cardId, idx) => (
                    <Box
                      key={idx}
                      onClick={() => setCurrentCardIndex(idx)}
                      sx={{
                        width: { xs: 18, sm: 22, md: 26, lg: 30 },
                        height: { xs: 18, sm: 22, md: 26, lg: 30 },
                        borderRadius: "50%",
                        border: "1.5px solid #9D2235",
                        bgcolor:
                          currentCardIndex === idx ? "#9D2235" : "transparent",
                        color: currentCardIndex === idx ? "#fff" : "#9D2235",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: {
                          xs: "0.6rem",
                          sm: "0.7rem",
                          md: "0.8rem",
                          lg: "0.9rem",
                        },
                        cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": {
                          bgcolor:
                            currentCardIndex === idx
                              ? "#7a182a"
                              : "rgba(157,34,53,0.08)",
                        },
                        "&:active": {},
                        flex: "0 0 auto",
                      }}
                    >
                      {idx + 1}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* RIGHT - Circle Clock, Timer and Notes */}
            <Box
              sx={{
                gridArea: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.3, sm: 0.6, md: 0.8 },
              }}
            >
              {/* Circle Clock - Above everything */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <EnhancedCountdown
                  size={20}
                  stroke={2}
                  progress={speakingTimerProgress}
                  showPie
                  showRing
                  idSuffix="speakingTime"
                />
              </Box>

              {/* Timer and Notes Row - Below circle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 0.4, sm: 0.8, md: 1 },
                }}
              >
                {/* Timer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 0.6, md: 0.7 },
                    px: { xs: 0.8, sm: 1, md: 1.2 },
                    py: { xs: 0.4, sm: 0.5, md: 0.6 },
                    borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                    minWidth: { xs: 70, sm: 90, md: 110 },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      fontSize: { xs: 16, sm: 20, md: 24 },
                      color: "#9D2235",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                      fontWeight: 800,
                      color: "#9D2235",
                      fontFamily: "'Roboto Mono', 'Courier New', monospace",
                      letterSpacing: { xs: 0.4, sm: 0.6, md: 0.8 },
                      lineHeight: 1,
                    }}
                  >
                    {formatSpeakingTimerDisplay()}
                  </Typography>
                </Box>

                {/* Skip Prep Button - only show when prep is active and not submitted */}
                {prepActive && !isSubmitted && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSkipPreparation}
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                      padding: { xs: "2px 6px", sm: "4px 8px", md: "6px 12px" },
                      minWidth: "auto",
                      borderColor: "#9D2235",
                      color: "#9D2235",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#7a182a",
                        bgcolor: "rgba(157, 34, 53, 0.05)",
                      },
                    }}
                    startIcon={
                      <SkipNextIcon
                        sx={{
                          fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                        }}
                      />
                    }
                  >
                    Skip Prep
                  </Button>
                )}

                {/* Already Submitted Message */}
                {isSubmitted && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "rgba(227, 242, 253, 0.95)",
                      borderRadius: 2,
                      border: "2px solid #90caf9",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ width: 20, height: 20, color: "#1976d2" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <Typography
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        fontWeight: 600,
                        color: "#1976d2",
                      }}
                    >
                      Test Already Submitted
                    </Typography>
                  </Box>
                )}

                {/* Notes Icon */}
                <Badge
                  badgeContent={notes?.length || 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#fff",
                      color: "#9D2235",
                      fontWeight: "bold",
                      fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.5rem" },
                      minWidth: { xs: 6, sm: 10, md: 12 },
                      height: { xs: 6, sm: 10, md: 12 },
                      boxShadow: "0 2px 4px rgba(157,34,53,0.2)",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => setNotesModalOpen(true)}
                    sx={{
                      background:
                        "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      boxShadow:
                        "0 4px 12px rgba(157,34,53,0.25), 0 2px 4px rgba(157,34,53,0.15)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                        transform: "translateY(-1px)",
                        boxShadow:
                          "0 6px 16px rgba(157,34,53,0.3), 0 4px 8px rgba(157,34,53,0.2)",
                      },
                      width: 38,
                      height: 38,
                      minWidth: 38,
                      minHeight: 38,
                      p: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <StickyNote2Icon
                      sx={{
                        fontSize: 18,
                        width: 18,
                        height: 18,
                        flexShrink: 0,
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                      }}
                    />
                  </IconButton>
                </Badge>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Main Speaking Content */}
      <Box
        className="w-full px-4 py-8"
        sx={{
          pt: "32px",
          transition: "padding-top 0.3s ease-in-out",
        }}
      >
        {/* Speaking Cards */}
        {speakingCards.length > 0 ? (
          <div className="flex flex-col md:flex-row gap-8 w-full mb-8">
            {speakingCards.map((card, index) => (
              <div
                key={card._id || index}
                className="w-full md:flex-1 bg-white border border-gray-200 rounded-lg shadow-md p-6"
              >
                <TextHighlighterWrapper onAddNote={handleAddNote}>
                  <div className="flex items-center mb-4">
                    <span className="px-3 py-1 text-sm font-semibold border border-[#9D2235] text-[#9D2235] rounded mr-3">
                      {card.cardType || `Card ${card.cardNumber}`}
                    </span>
                    <HtmlRenderer
                      content={card.roleLabel}
                      component="span"
                      sx={{
                        fontSize: "1.125rem",
                        fontWeight: 700,
                        color: "#9D2235",
                      }}
                    />
                  </div>
                  {card.setting && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-500 font-semibold">
                        Setting:
                      </span>
                      <HtmlRenderer
                        content={card.setting}
                        component="div"
                        sx={{ fontWeight: 500 }}
                      />
                    </div>
                  )}
                  {card.description && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-500 font-semibold mb-1 block">
                        Description:
                      </span>
                      <HtmlRenderer
                        content={card.description}
                        component="div"
                        sx={{
                          fontSize: "1rem",
                          lineHeight: 1.6,
                        }}
                      />
                    </div>
                  )}
                  {card.tasks && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-500 font-semibold mb-1 block">
                        Tasks:
                      </span>
                      <HtmlRenderer
                        content={card.tasks}
                        component="div"
                        sx={{
                          whiteSpace: "pre-line",
                          backgroundColor: "#f5f5f5",
                          padding: 1.5,
                          borderRadius: 1,
                          fontSize: "1rem",
                          lineHeight: 1.6,
                        }}
                      />
                    </div>
                  )}
                </TextHighlighterWrapper>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mb-8">
            <div className="text-lg font-bold mb-2">
              Speaking Test - Part {currentCardIndex + 1}
            </div>
            <div className="text-base">
              Prepare your speaking response for Part {currentCardIndex + 1}.
            </div>
          </div>
        )}

        {/* Recording/Upload Interface */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            {/* === Audio preview with enhanced controls === */}
            {recordedUrl && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 4,
                  gap: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#9D2235", fontWeight: 600 }}
                >
                  🎵 Audio Preview
                </Typography>

                {/* Custom playback controls */}

                {/* Hidden audio element for control */}
                <audio
                  ref={audioRef}
                  src={recordedUrl}
                  onEnded={handleAudioEnded}
                  style={{ display: "none" }}
                />

                {/* Fallback native controls for more advanced features */}
                <audio
                  controls
                  src={recordedUrl}
                  style={{ width: "100%", maxWidth: 400 }}
                />
              </Box>
            )}

            {/* Record/Upload options in row-cards */}
            <Box className="flex flex-col md:flex-row gap-6 justify-center mb-3">
              {/* Show only Record Card if "record" was selected */}
              {speakingAudioMethod === "record" && (
                <Box className="flex-1 bg-gray-50 rounded-2xl shadow p-4 sm:p-6 flex flex-col items-center border border-gray-200 w-full sm:min-w-[250px] sm:max-w-[500px] mx-auto">
                  <Typography
                    variant="h6"
                    className="mb-2 text-center text-[#9D2235] font-semibold"
                  >
                    Record Your Response
                  </Typography>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <IconButton
                      onClick={handleStart}
                      disabled={prepActive || isSubmitted || (isRecording && !isPaused)}
                      color="primary"
                      size="large"
                      sx={{
                        bgcolor:
                          isRecording && !isPaused
                            ? "primary.main"
                            : "transparent",
                        color:
                          isRecording && !isPaused ? "white" : "primary.main",
                        border: "2px solid",
                        borderColor: "primary.main",
                      }}
                    >
                      {(prepActive || isSubmitted) ? <LockIcon /> : <MicIcon />}
                    </IconButton>
                    <IconButton
                      onClick={handlePause}
                      disabled={prepActive || isSubmitted || !isRecording || isPaused}
                      color="secondary"
                      size="large"
                      sx={{
                        border: "2px solid",
                        borderColor: "secondary.main",
                      }}
                    >
                      <PauseIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleStop}
                      disabled={prepActive || isSubmitted || (!isRecording && !isPaused)}
                      color="error"
                      size="large"
                      sx={{ border: "2px solid", borderColor: "error.main" }}
                    >
                      <StopIcon />
                    </IconButton>
                    {(recordedUrl || uploadedFile) && (
                      <IconButton
                        onClick={handleDeleteRecording}
                        disabled={isSubmitted}
                        color="error"
                        size="large"
                        sx={{ border: "2px solid", borderColor: "error.main" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  {isSubmitted && (
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      className="mt-3 text-center"
                    >
                      🔒 Already Submitted
                    </Typography>
                  )}
                  {!isSubmitted && isRecording && (
                    <Typography
                      color="error"
                      variant="body2"
                      className="mt-3 text-center"
                    >
                      🔴 Recording... {isPaused ? "(Paused)" : ""}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Show only Upload Card if "upload" was selected */}
              {speakingAudioMethod === "upload" && (
                <Box className="flex-1 bg-gray-50 rounded-2xl shadow p-4 sm:p-6 flex flex-col items-center border border-gray-200 w-full sm:min-w-[250px] sm:max-w-[500px] mx-auto">
                  <Typography
                    variant="h6"
                    className="mb-2 text-center text-[#9D2235] font-semibold"
                  >
                    Upload an Audio File
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*,video/mp4,.mp4"
                    onChange={handleFileUpload}
                    className="block w-full text-sm p-2 border rounded cursor-pointer bg-white"
                    disabled={prepActive || isSubmitted}
                  />
                  {(prepActive || isSubmitted) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <LockIcon sx={{ color: "#9D2235" }} />
                      <Typography variant="caption" color="text.secondary">
                        {isSubmitted ? "Already submitted" : "Locked during preparation"}
                      </Typography>
                    </Box>
                  )}
                  {uploadedFile && (
                    <Typography
                      variant="body2"
                      color="success.main"
                      className="mt-2 text-center font-medium"
                    >
                      ✅ File uploaded: {uploadedFile.name}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Fallback: Show both options if no method specified (backward compatibility) */}
              {!speakingAudioMethod && (
                <>
                  {/* Record Card */}
                  <Box className="flex-1 bg-gray-50 rounded-2xl shadow p-4 sm:p-6 flex flex-col items-center border border-gray-200 w-full sm:min-w-[250px]">
                    <Typography
                      variant="h6"
                      className="mb-2 text-center text-[#9D2235] font-semibold"
                    >
                      Record Your Response
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                    >
                      <IconButton
                        onClick={handleStart}
                        disabled={prepActive || isSubmitted || (isRecording && !isPaused)}
                        color="primary"
                        size="large"
                        sx={{
                          bgcolor:
                            isRecording && !isPaused
                              ? "primary.main"
                              : "transparent",
                          color:
                            isRecording && !isPaused ? "white" : "primary.main",
                          border: "2px solid",
                          borderColor: "primary.main",
                        }}
                      >
                        {(prepActive || isSubmitted) ? <LockIcon /> : <MicIcon />}
                      </IconButton>
                      <IconButton
                        onClick={handlePause}
                        disabled={prepActive || isSubmitted || !isRecording || isPaused}
                        color="secondary"
                        size="large"
                        sx={{
                          border: "2px solid",
                          borderColor: "secondary.main",
                        }}
                      >
                        <PauseIcon />
                      </IconButton>
                      <IconButton
                        onClick={handleStop}
                        disabled={prepActive || isSubmitted || (!isRecording && !isPaused)}
                        color="error"
                        size="large"
                        sx={{ border: "2px solid", borderColor: "error.main" }}
                      >
                        <StopIcon />
                      </IconButton>
                      {(recordedUrl || uploadedFile) && (
                        <IconButton
                          onClick={handleDeleteRecording}
                          disabled={isSubmitted}
                          color="error"
                          size="large"
                          sx={{
                            border: "2px solid",
                            borderColor: "error.main",
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    {isSubmitted && (
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        className="mt-3 text-center"
                      >
                        🔒 Already Submitted
                      </Typography>
                    )}
                    {!isSubmitted && isRecording && (
                      <Typography
                        color="error"
                        variant="body2"
                        className="mt-3 text-center"
                      >
                        🔴 Recording... {isPaused ? "(Paused)" : ""}
                      </Typography>
                    )}
                  </Box>

                  {/* Upload Card */}
                  <Box className="flex-1 bg-gray-50 rounded-2xl shadow p-4 sm:p-6 flex flex-col items-center border border-gray-200 w-full sm:min-w-[250px]">
                    <Typography
                      variant="h6"
                      className="mb-2 text-center text-[#9D2235] font-semibold"
                    >
                      Or Upload an Audio File
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="audio/*"
                      onChange={handleFileUpload}
                      className="block w-full text-sm p-2 border rounded cursor-pointer bg-white"
                      disabled={prepActive || isSubmitted}
                    />
                    {(prepActive || isSubmitted) && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <LockIcon sx={{ color: "#9D2235" }} />
                        <Typography variant="caption" color="text.secondary">
                          {isSubmitted ? "Already submitted" : "Locked during preparation"}
                        </Typography>
                      </Box>
                    )}
                    {uploadedFile && (
                      <Typography
                        variant="body2"
                        color="success.main"
                        className="mt-2 text-center font-medium"
                      >
                        ✅ File uploaded: {uploadedFile.name}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>

            {/* Progress, Divider, etc. */}
            {(isUploading || isSubmitting) && (
              <Box sx={{ mt: 2, mb: 1, textAlign: "center" }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {isUploading
                    ? "Uploading audio..."
                    : isSubmitting
                    ? "Submitting your answers..."
                    : ""}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Fixed Footer Navigation Actions */}
        <Box
          sx={{
            position: "fixed",
            left: 0,
            bottom: 0,
            width: "100%",
            bgcolor: "#F7F7F7",
            boxShadow: "0px -4px 12px rgba(0,0,0,0.05)",
            px: { xs: 2, sm: 4 },
            py: 2,
            zIndex: 30,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {!isLastCard ? (
            <Button
              variant="contained"
              onClick={handleNextOrSubmit}
              disabled={
                (!recordedBlob && !uploadedFile) || isUploading || isSubmitting
              }
              endIcon={<ArrowForwardIcon />}
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                fontSize: "1.1rem",
                fontWeight: "bold",
                backgroundColor: "#9D2235",
                "&:hover": {
                  backgroundColor: "#7a182a",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                },
              }}
            >
              {isUploading || isSubmitting
                ? "Processing..."
                : `Next Part (${currentCardIndex + 2})`}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNextOrSubmit}
              disabled={
                (!recordedBlob && !uploadedFile) || isUploading || isSubmitting
              }
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                fontSize: "1.1rem",
                fontWeight: "bold",
                backgroundColor: "#9D2235",
                "&:hover": {
                  backgroundColor: "#7a182a",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                },
              }}
            >
              {isUploading || isSubmitting
                ? "Submitting..."
                : "Submit Speaking"}
            </Button>
          )}
        </Box>

        {/* ...existing code... */}

        {/* Success Dialog */}
        <Dialog open={successOpen}>
          <DialogTitle>Success!</DialogTitle>
          <Box sx={{ p: 2 }}>
            <Typography>
              Your speaking responses have been submitted successfully.
            </Typography>
          </Box>
        </Dialog>

        {/* Submission Response Popup */}
        <Dialog
          open={submissionPopupOpen}
          onClose={handleCloseSubmissionPopup}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: "80vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              bgcolor: "#9D2235", // Use consistent theme color for all states
              color: "white",
              py: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              {submissionResponse?.data?.message &&
              submissionResponse.data.message.includes("already completed") ? (
                <WarningIcon sx={{ fontSize: 28 }} />
              ) : submissionResponse?.data?.success === 0 ||
                submissionResponse?.status >= 400 ||
                submissionResponse?.status === "error" ? (
                <ErrorIcon sx={{ fontSize: 28 }} />
              ) : (
                <CheckCircleIcon sx={{ fontSize: 28 }} />
              )}
              <Typography variant="h6" fontWeight="bold">
                {submissionResponse?.data?.message &&
                submissionResponse.data.message.includes("already completed")
                  ? "Submission Notice"
                  : submissionResponse?.data?.success === 0 ||
                    submissionResponse?.status >= 400 ||
                    submissionResponse?.status === "error"
                  ? "Upload Error"
                  : "Submission Message"}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {/* Handle different response types - Check for 'already completed' first */}
            {submissionResponse?.data?.message &&
            submissionResponse.data.message.includes("already completed") ? (
              <Alert
                severity="warning"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid #ffecb3",
                }}
              >
                <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
                  Test Already Completed
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {submissionResponse.data.message}
                </Typography>
              </Alert>
            ) : submissionResponse?.data?.success === 0 ||
              submissionResponse?.status >= 400 ||
              submissionResponse?.status === "error" ? (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid #ffcdd2",
                }}
              >
                <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
                  Upload Failed
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {/* Extract specific error message */}
                  {submissionResponse?.data?.data?.error ||
                    submissionResponse?.data?.message ||
                    submissionResponse?.message ||
                    "An error occurred while submitting your response."}
                </Typography>
              </Alert>
            ) : (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid #c8e6c9",
                }}
              >
                <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
                  Message !
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {/* Display actual API response message */}
                  {submissionResponse?.data?.message ||
                    submissionResponse?.message ||
                    "Your audio responses are now being processed for evaluation."}
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: "center" }}>
            {/* Show different button based on response type - Check for 'already completed' first */}
            {(submissionResponse?.data?.message &&
              submissionResponse.data.message.includes("already completed")) ||
            submissionResponse?.data?.success === 0 ||
            submissionResponse?.status >= 400 ||
            submissionResponse?.status === "error" ? (
              <Button
                onClick={() => setSubmissionPopupOpen(false)}
                variant="contained"
                size="large"
                startIcon={<RefreshIcon />}
                sx={{
                  backgroundColor: "#9D2235",
                  "&:hover": {
                    backgroundColor: "#7a182a",
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                Try Again
              </Button>
            ) : (
              <Button
                onClick={handleCloseSubmissionPopup}
                variant="contained"
                size="large"
                startIcon={<CheckCircleIcon />}
                sx={{
                  backgroundColor: "#9D2235",
                  "&:hover": {
                    backgroundColor: "#7a182a",
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                Return to Test Menu
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Preparation Time Ended Popup */}
        <Dialog open={prepEndedPopupOpen} onClose={() => setPrepEndedPopupOpen(false)}>
          <DialogTitle sx={{ bgcolor: "#9D2235", color: "white", textAlign: "center", py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">Preparation Time Ended</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Your 3-minute preparation time has ended.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You may now start recording your response.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={() => setPrepEndedPopupOpen(false)}
              variant="contained"
              sx={{ backgroundColor: "#9D2235", "&:hover": { backgroundColor: "#7a182a" } }}
            >
              OK, Start Recording
            </Button>
          </DialogActions>
        </Dialog>

        {/* Recording Time Ended Popup */}
        <Dialog open={recordEndedPopupOpen} onClose={() => setRecordEndedPopupOpen(false)}>
          <DialogTitle sx={{ bgcolor: "#9D2235", color: "white", textAlign: "center", py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <MicIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">Recording Complete</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Audio has been recorded for 6 minutes.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your recording has been automatically saved. You can now submit your response.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={() => setRecordEndedPopupOpen(false)}
              variant="contained"
              sx={{ backgroundColor: "#9D2235", "&:hover": { backgroundColor: "#7a182a" } }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notes Modal */}
        <NotesModal
          open={notesModalOpen}
          onClose={() => setNotesModalOpen(false)}
          notes={notes}
        />
      </Box>
    </>
  );
}
